import {
  first,
  flatMap,
  filter as filterLodash,
  map,
  reduce,
  upperFirst,
  toNumber,
  includes,
} from "lodash";
import type { DataItemProps } from "@/shared/widgets/DataSection";
import { Person } from "@/features/people/graphql/generated";
import {
  useAttachmentsQuery,
  Attachment,
} from "@/shared/services/api/graphql/generated";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import {
  DimensionWithScores,
  useDimensionsWithScores,
} from "./useDimensionsWithScores";
import { useModal } from "@/shared/utils/hooks/useModal";
import { IconName } from "@/shared/ui/Icon";
import { ModalFileName } from "@/shared/modals";
import {
  useAttachmentDelete,
  useAttachmentRead,
} from "@/shared/hooks/content/useAttachments";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";
import { useTranslation } from "react-i18next";

type DataFilter = {
  position_id?: number;
  person_id?: number;
};

const getHasDimensionScores = (dimensionScoresData: DimensionWithScores[]) =>
  filterLodash(dimensionScoresData, (d) => !!d.score?.value)?.length > 0;

const getSurveyLabelAndTag = (
  t: (key: string) => string,
  surveyType: string
) => {
  const SURVEY_LABELS_AND_TAGS: Partial<
    Record<AssessmentSurveyType, { label: string; tag: string }>
  > = {
    [AssessmentSurveyType.MAP]: {
      label: "MAP",
      tag: t("survey.tags.personality"),
    },
    [AssessmentSurveyType.MATCH_V_V2]: {
      label: "MATCH_V",
      tag: t("profile.values"),
    },
    [AssessmentSurveyType.AP_COMPETENCIES]: {
      label: t("survey.ap_competencies_profile_label"),
      tag: t("survey.tags.competencies"),
    },
    [AssessmentSurveyType.IPIP_NEO_BIG_5]: {
      label: t("survey.personality"),
      tag: t("survey.tags.personality"),
    },
    [AssessmentSurveyType.SHWARTZ_VALUES]: {
      label: t("survey.values"),
      tag: t("profile.values"),
    },
    [AssessmentSurveyType.WISNIO_COMPETENCIES]: {
      label: t("survey.competencies"),
      tag: t("survey.tags.competencies"),
    },
  };

  return (
    SURVEY_LABELS_AND_TAGS[surveyType as AssessmentSurveyType] || {
      label: t("survey.default_label"),
      tag: t("survey.default_tag"),
    }
  );
};

interface UsePersonProfileDataProps {
  person: Person;
  filter?: DataFilter;
  surveyConfig?: AssessmentSurveyType[];
}

export const usePersonProfileData = ({
  person,
  filter = {},
  surveyConfig,
}: UsePersonProfileDataProps) => {
  const { t } = useTranslation();
  const { account } = useAccount();

  const [openScoreEditModal] = useModal(ModalFileName.SCORES_EDIT);

  const { handleDeleteAttachment } = useAttachmentDelete({
    person_id: person?.id!,
    position_id: toNumber(filter?.position_id),
  });

  const { readAttachment } = useAttachmentRead({
    person_id: person?.id!,
    position_id: toNumber(filter?.position_id),
  });

  const { data: addonData, loading } = useAttachmentsQuery({
    variables: {
      attachmentsFilter: {
        person_id: person?.id!,
        position_id: filter?.position_id,
      },
    },
    skip: !person?.id,
  });

  const { attachments } = addonData || {};
  const { dimensionsData } = useDimensionsWithScores(
    {
      people: [person?.id!],
      position_id: filter?.position_id,
    },
    [person]
  );

  // This is for RRA and maybe others who have custom Dimensions configured
  const dimensionScoresData = reduce(
    account.dimensions,
    (acc, dimension) => {
      const groups = dimension?.groups || [];
      const data = flatMap(groups, (group) => dimensionsData?.[group]).filter(
        Boolean
      );
      return {
        ...acc,
        [dimension?.code]: data,
      };
    },
    {} as { [key: string]: DimensionWithScores[] }
  );

  const addableDataItems = map(account.dimensions, (dimension) => ({
    label: t("common.scores_label", {
      label: upperFirst(dimension?.code),
    }),
    icon: "Score" as IconName,
    onClick: () =>
      openScoreEditModal({
        data: dimensionScoresData[dimension?.code!] || [],
        title: t("common.scores_label", {
          label: upperFirst(dimension?.code),
        }),
        personId: person?.id!,
        positionId: filter?.position_id,
        dimensionGroup: dimension?.code,
      }),
    hidden: getHasDimensionScores(dimensionScoresData[dimension?.code!] || []),
  })).filter((item: any) => !item.hidden);

  const completedSurveyItems = map(surveyConfig, (surveyType) => {
    const { label, tag } = getSurveyLabelAndTag(t, surveyType || "");

    return {
      type: "survey",
      label,
      icon: includes(person?.completed_dim_types, surveyType) && "CheckGreen",
      visible: true,
      tagLabel: tag,
      editable: false,
      removable: false,
    };
  });

  const dataList = [
    ...map(dimensionScoresData, (_data, key) => ({
      type: "score",
      label: t("common.scores_label", { label: upperFirst(key) }),
      icon: "Score",
      editable: true,
      removable: false,
      tagLabel: upperFirst(key),
      visible: getHasDimensionScores(_data),
      onClick: () =>
        openScoreEditModal({
          data: _data,
          title: t("common.scores_label", {
            label: upperFirst(key),
          }),
          personId: person?.id!,
          positionId: filter?.position_id,
          dimensionGroup: key,
        }),
    })),
    ...completedSurveyItems,
    ...map(attachments, (attachment) => ({
      type: "file",
      id: attachment?.id || null,
      label: attachment?.file_name || "",
      icon: "File",
      editable: false,
      removable: true,
      visible: true,
      onClick: () => readAttachment(attachment?.id!),
      onRemove: () => handleDeleteAttachment(attachment?.id!),
      tagLabel: upperFirst(first(attachment?.tags || [])?.replace("_", " ")),
    })),
  ].filter((item: any) => item.visible) as DataItemProps[];

  return {
    attachments: (attachments || []) as Attachment[],
    dimensionScoresData,
    loading,
    dataList,
    addableDataItems,
  };
};
